import { useEffect, useRef } from "react";

// Phones and tablets: the Back button / swipe-back closes whatever is on top (menu, chat, dialog)
// before it leaves the page, like a native app.
//
// Each open overlay adds a history entry for the same URL, tagged in history.state. Back pops that
// entry and the overlay closes. Closing it any other way (its X, tapping outside) pops the entry
// itself, so history doesn't fill up with dead entries. React Router sees same-URL pops and ignores them.

const STATE_KEY = "irgOverlays";
const TOUCH_OR_PHONE = "(max-width: 767px), (hover: none) and (pointer: coarse)";

const stack = []; // open overlays, oldest first: { token, close }
let pendingBacks = 0; // history.back() calls we made and haven't seen the popstate for yet
let queued = []; // tokens opened while a back was pending; pushed once it lands
let nextToken = 1;
let listening = false;

const tokensInHistory = () => {
  const list = window.history.state?.[STATE_KEY];
  return Array.isArray(list) ? list : [];
};

function pushEntry(token) {
  const state = window.history.state || {};
  window.history.pushState({ ...state, [STATE_KEY]: [...tokensInHistory(), token] }, "");
}

function onPopState() {
  if (pendingBacks > 0) {
    pendingBacks -= 1;
    if (pendingBacks === 0 && queued.length) {
      const toPush = queued;
      queued = [];
      toPush.forEach(pushEntry);
    }
    return;
  }
  // The user went back: close every overlay whose entry is gone, topmost first.
  const live = tokensInHistory();
  for (let i = stack.length - 1; i >= 0; i -= 1) {
    const entry = stack[i];
    if (!live.includes(entry.token)) {
      stack.splice(i, 1);
      entry.close();
    }
  }
}

function open(close) {
  if (!listening) {
    window.addEventListener("popstate", onPopState);
    listening = true;
  }
  const token = nextToken;
  nextToken += 1;
  stack.push({ token, close });
  if (pendingBacks > 0) queued.push(token);
  else pushEntry(token);
  return token;
}

// The overlay closed some other way: drop its history entry if it's still the top one.
function release(token) {
  const index = stack.findIndex((entry) => entry.token === token);
  if (index === -1) return; // already closed by Back
  stack.splice(index, 1);
  if (queued.includes(token)) {
    queued = queued.filter((t) => t !== token);
    return;
  }
  const live = tokensInHistory();
  if (live[live.length - 1] === token) {
    pendingBacks += 1;
    window.history.back();
  }
}

/**
 * While `open`, Back calls `onClose` instead of leaving the page. Phones and tablets only.
 * @param {boolean} isOpen
 * @param {() => void} onClose
 */
export function useBackToClose(isOpen, onClose) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!isOpen || !window.matchMedia?.(TOUCH_OR_PHONE).matches) return undefined;
    const token = open(() => closeRef.current?.());
    return () => release(token);
  }, [isOpen]);
}

// Leaving with a link while an overlay is open: replace the overlay's history entry instead of
// adding one, so Back from the new page returns to the previous page, not the same page again.
export const overlayIsOnTop = () => tokensInHistory().length > 0;
